/**
 * Driver dropdown options for route edit and medical driver assignment.
 * Permission-based only (no role-based "driver"); merges internal employees with external users.
 */

export const isDriverEligibleEmployee = (employee) => {
  if (!employee || employee.status !== 'active') return false;
  const permissions = Array.isArray(employee.permissions)
    ? employee.permissions.map((p) => `${p || ''}`.toLowerCase())
    : [];
  return permissions.includes('isdriver') || permissions.includes('is_driver');
};

const cleanNamePart = (value) => `${value ?? ""}`.trim();

export const driverLastName = (driver) => {
  const last = cleanNamePart(driver?.lastname);
  if (last) return last;
  const name = cleanNamePart(driver?.name);
  if (name.includes(",")) return cleanNamePart(name.split(",")[0]);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 1];
  return name;
};

export const driverFirstName = (driver) => {
  const first = cleanNamePart(driver?.firstname);
  if (first) return first;
  const name = cleanNamePart(driver?.name);
  if (name.includes(",")) return cleanNamePart(name.split(",").slice(1).join(","));
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return parts.slice(0, -1).join(" ");
  return "";
};

export const driverPreferredName = (driver) =>
  cleanNamePart(driver?.preferred_name) || cleanNamePart(driver?.name_cn);

/** Last Name, First Name (Preferred Name). Preferred name is omitted when blank. */
export const formatDriverDropdownLabel = (driver) => {
  const last = driverLastName(driver);
  const first = driverFirstName(driver);
  const preferred = driverPreferredName(driver);
  const core = [last, first].filter(Boolean).join(", ") || cleanNamePart(driver?.name) || cleanNamePart(driver?.username);
  if (!core) return "";
  return preferred ? `${core} (${preferred})` : core;
};

export const compareDriversByLastName = (a, b) => {
  const byLast = driverLastName(a).localeCompare(driverLastName(b), undefined, { sensitivity: "base" });
  if (byLast !== 0) return byLast;
  return driverFirstName(a).localeCompare(driverFirstName(b), undefined, { sensitivity: "base" });
};

export const sortDriversByLastName = (drivers = []) =>
  [...(drivers || [])].sort(compareDriversByLastName);

export const buildExternalDriverOptions = (records = []) => {
  return (records || [])
    .filter((record) => Array.isArray(record?.permissions) && record.permissions.includes('isDriver'))
    .map((record) => ({
      id: record?.external_user_id,
      name: record?.name || record?.username || '',
      firstname: record?.firstname || '',
      lastname: record?.lastname || '',
      preferred_name: record?.preferred_name || '',
      name_cn: record?.name_cn || '',
      title: record?.title || '',
      employment_status: 'external',
      license_type: '',
      phone: '',
      email: record?.email || '',
      status: 'active',
    }))
    .filter((record) => record.id && (record.name || record.lastname || record.firstname));
};

export const mergeDriverOptionsById = (internalDrivers, externalDrivers) => {
  const mergedById = new Map();
  [...internalDrivers, ...externalDrivers].forEach((driverItem) => {
    if (!driverItem?.id) return;
    if (!mergedById.has(driverItem.id)) {
      mergedById.set(driverItem.id, driverItem);
    }
  });
  return sortDriversByLastName(Array.from(mergedById.values()));
};

/** For route cards / lists: map external_user_id → display name from permissions-list payload. */
export const buildExternalDriverNameByIdMap = (records = []) => {
  const out = Object.create(null);
  (records || []).forEach((r) => {
    const id = r?.external_user_id;
    if (id === undefined || id === null || id === '') return;
    const name = (r?.name || r?.username || '').trim();
    if (!name) return;
    out[String(id)] = name;
  });
  return out;
};

/** Match route.driver when it is an external_user_id (HR / portal user), not an employees row id. */
export const findExternalDriverRecordByExternalUserId = (records = [], externalUserId) => {
  if (externalUserId === undefined || externalUserId === null || externalUserId === '') {
    return undefined;
  }
  const target = `${externalUserId}`;
  const record = (records || []).find((r) => `${r?.external_user_id ?? ''}` === target);
  if (!record) return undefined;
  return {
    id: record.external_user_id,
    name: record.name || record.username || '',
    name_cn: '',
    title: record.title || '',
    employment_status: 'external',
    license_type: '',
    phone: '',
    email: record.email || '',
    status: 'active',
  };
};
